import { Button, Checkbox, IconButton, Icons, Input, Tooltip } from '@healthinal/ui';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useCreateAddress, useUpdateAddress } from '../../../../api/created/address.ts';
import {
  getGetAddressesQueryKey,
  useGetAddresses,
  useSetAddressStatus,
} from '../../../../api/generated/address-controller/address-controller.ts';
import type { AddressDto } from '../../../../api/generated/model';
import { AddressDtoAddressType } from '../../../../api/generated/model';
import { queryClient } from '../../../../api/queryClient.ts';
import { TruncateText } from '../../../../components/TruncateText.tsx';
import { CreateAddressDialog } from '../../../components/patient-address/CreateAddressDialog.tsx';
import { InstitutionHistoryModal } from './InstitutionHistoryModal.tsx';
import { MasterdataTable } from './MasterTable.tsx';

type AddressDialogState = {
  open: boolean;
  address: AddressDto | null;
};

type HistoryModalState = {
  open: boolean;
  address: AddressDto | null;
};

const availableAddressTypes = Object.values(AddressDtoAddressType).filter(
  (type) =>
    type !== AddressDtoAddressType.ACCOMMODATION_PRIVATE &&
    type !== AddressDtoAddressType.RELATIVE &&
    type !== AddressDtoAddressType.CONFIDANT &&
    type !== AddressDtoAddressType.CONTACT_REFERENCE_PERSON,
);

type AddressDtoWithCanton = AddressDto & { canton?: string | null };

function InstitutionAddressTableHeader() {
  return (
    <>
      <th style={{ width: 180 }}>Institution</th>
      <th style={{ width: 150 }}>Telefon Nr.</th>
      <th style={{ width: 220 }}>E-Mail</th>
      <th style={{ width: 180 }}>Homepage</th>
      <th style={{ width: 160 }}>Strasse</th>
      <th style={{ width: 180 }}>Zusatz</th>
      <th style={{ width: 90 }}>PLZ</th>
      <th style={{ width: 120 }}>Ort</th>
      <th style={{ width: 80 }}>Kanton</th>
      <th style={{ width: 65 }}>Aktiv</th>
      <th style={{ width: 100 }}>Verlauf</th>
    </>
  );
}

function getZusatz(address: AddressDto) {
  const extended = address as AddressDto & {
    zusatz?: string | null;
    addressLine2?: string | null;
    additionalLine?: string | null;
    supplement?: string | null;
    addition?: string | null;
    extra?: string | null;
  };

  return (
    extended.zusatz ??
    extended.addressLine2 ??
    extended.additionalLine ??
    extended.supplement ??
    extended.addition ??
    extended.extra ??
    '-'
  );
}

function InstitutionAddressTableData({
  address,
  onToggleActive,
  onShowHistory,
  isPending,
}: {
  address: AddressDtoWithCanton;
  onToggleActive?: (address: AddressDto) => void;
  onShowHistory?: (address: AddressDto) => void;
  isPending?: boolean;
}) {
  return (
    <>
      <td>
        <TruncateText>{address.organization ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.phone ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.email ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.homepage ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.address ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{getZusatz(address)}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.zip ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.city ?? '-'}</TruncateText>
      </td>
      <td>
        <TruncateText>{address.canton ?? '-'}</TruncateText>
      </td>
      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <Checkbox checked={!!address.isActive} disabled={isPending} onClick={() => onToggleActive?.(address)} />
      </td>
      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
        <Tooltip title="Verlauf">
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onShowHistory?.(address);
            }}>
            <Icons.History />
          </IconButton>
        </Tooltip>
      </td>
    </>
  );
}

export function InstitutionAddressTable() {
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [search, setSearch] = useState('');
  const [dialogState, setDialogState] = useState<AddressDialogState>({ open: false, address: null });
  const [historyModalState, setHistoryModalState] = useState<HistoryModalState>({ open: false, address: null });

  const { data: addresses = [], isLoading } = useGetAddresses({
    includeInactive: true,
    search: search.trim() || undefined,
  });

  const createAddressMutation = useCreateAddress({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetAddressesQueryKey({ includeInactive: true }),
        });
        toast.success('Adresse erfolgreich erstellt');
      },
      onError: () => {
        toast.error('Beim Erstellen der Adresse ist ein Fehler aufgetreten');
      },
    },
  });

  const updateAddressMutation = useUpdateAddress({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetAddressesQueryKey({ includeInactive: true }),
        });
        toast.success('Einrichtung erfolgreich bearbeitet');
      },
      onError: () => {
        toast.error('Beim Bearbeiten der Einrichtung ist ein Fehler aufgetreten');
      },
    },
  });

  const setAddressStatusMutation = useSetAddressStatus({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetAddressesQueryKey({ includeInactive: true }),
        });
      },
      onError: () => {
        toast.error('Beim (De-)Aktivieren der Institution ist ein Fehler aufgetreten');
      },
    },
  });

  const filteredAddresses = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return addresses;

    return addresses.filter((address) => {
      const values = [
        address.organization,
        address.phone,
        address.email,
        address.homepage,
        address.address,
        getZusatz(address),
        address.zip,
        address.city,
        (address as AddressDtoWithCanton).canton,
      ];

      return values.some((value) => value != null && String(value).toLowerCase().includes(term));
    });
  }, [addresses, search]);

  const totalCount = filteredAddresses.length;
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / size) : 0;

  const paginatedAddresses = useMemo(() => {
    const start = page * size;
    return filteredAddresses.slice(start, start + size);
  }, [filteredAddresses, page, size]);

  const handleToggleActive = (address: AddressDto) => {
    if (!address.id) return;

    setAddressStatusMutation.mutate({
      id: address.id,
      params: {
        isActive: !address.isActive,
      },
    });
  };

  return (
    <>
      <MasterdataTable
        title="Institutionen"
        headerActions={
          <>
            <Input
              placeholder="Suche..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              sx={{
                width: 260,
                maxWidth: '100%',
                flexShrink: 0,
              }}
            />

            <Button
              color="primary"
              endDecorator={<Icons.Add />}
              onClick={() => setDialogState({ open: true, address: null })}>
              Institution erstellen
            </Button>
          </>
        }
        isLoading={isLoading}
        isEmpty={paginatedAddresses.length === 0}
        emptyText="Keine Daten vorhanden."
        colSpan={11}
        page={page}
        totalPages={totalPages}
        onPrevPage={() => setPage((prev) => Math.max(0, prev - 1))}
        onNextPage={() => setPage((prev) => Math.min(Math.max(0, totalPages - 1), prev + 1))}
        disablePrev={page === 0 || isLoading}
        disableNext={page + 1 >= totalPages || isLoading}
        header={<InstitutionAddressTableHeader />}>
        {paginatedAddresses.map((address, index) => (
          <tr
            key={address.id ?? `${address.organization}-${address.address}-${index}`}
            onClick={() => setDialogState({ open: true, address })}
            style={{ cursor: 'pointer' }}>
            <InstitutionAddressTableData
              address={address}
              onToggleActive={handleToggleActive}
              onShowHistory={(selectedAddress) => setHistoryModalState({ open: true, address: selectedAddress })}
              isPending={setAddressStatusMutation.isPending}
            />
          </tr>
        ))}
      </MasterdataTable>

      <CreateAddressDialog
        open={dialogState.open}
        address={dialogState.address}
        isLoading={createAddressMutation.isPending || updateAddressMutation.isPending}
        onClose={(addressDto) => {
          setDialogState({ open: false, address: null });

          if (!addressDto) return;

          if (addressDto.id) {
            updateAddressMutation.mutate({ data: addressDto });
          } else {
            createAddressMutation.mutate({ data: addressDto });
          }
        }}
        selectedAddressTypes={availableAddressTypes}
        canChangeAddressType={true}
      />

      <InstitutionHistoryModal
        open={historyModalState.open}
        onClose={() => setHistoryModalState({ open: false, address: null })}
        address={historyModalState.address}
      />
    </>
  );
}
